import { useId, useState } from "react";
import { Loader2, PlugZap, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  AnchoredDetailCalloutBody,
  AnchoredDetailCalloutFieldLabel,
  AnchoredDetailCalloutFooter,
  AnchoredDetailCalloutTitle,
  DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_MIN_HEIGHT_PX,
  DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_WIDTH_PX,
  FormAnchoredDetailCallout,
} from "@/components/ui/AnchoredDetailFormCallout";
import { buildAttributeAnchorSelector } from "@/components/ui/AnchoredDetailCallout";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";

export const BENCH_CONNECTION_STATUS_ANCHOR_ATTRIBUTE = "data-bench-connection-status";

export function benchConnectionStatusAnchorSelector(anchorId: string): string {
  return buildAttributeAnchorSelector(BENCH_CONNECTION_STATUS_ANCHOR_ATTRIBUTE, anchorId);
}

function statusLabel(controlLevel: "local" | "remote" | "unknown", loading: boolean): string {
  if (loading) return "Checking bench…";
  if (controlLevel === "local") return "Local actuation";
  if (controlLevel === "remote") return "Remote observe only";
  return "Bench unknown";
}

function statusTone(controlLevel: "local" | "remote" | "unknown", loading: boolean): "success" | "warning" | "muted" {
  if (loading) return "muted";
  if (controlLevel === "local") return "success";
  return "warning";
}

export function BenchConnectionStatusButton({
  port,
  draftPort,
  onDraftPortChange,
  onApplyPort,
  controlLevel,
  loading,
  localBenchId,
  controlMessage,
  reloadPending,
  onReload,
}: {
  port: string;
  draftPort: string;
  onDraftPortChange: (value: string) => void;
  onApplyPort: () => void;
  controlLevel: "local" | "remote" | "unknown";
  loading: boolean;
  localBenchId: string | null;
  controlMessage: string | null;
  reloadPending: boolean;
  onReload: () => void;
}): JSX.Element {
  const anchorId = useId();
  const [open, setOpen] = useState(false);
  const anchorSelector = benchConnectionStatusAnchorSelector(anchorId);
  const tone = statusTone(controlLevel, loading);
  const portDirty = draftPort.trim() !== port.trim();

  return (
    <>
      <button
        type="button"
        data-bench-connection-status={anchorId}
        className={cn(
          "relative inline-flex h-9 w-9 items-center justify-center rounded-md border bg-background text-foreground shadow-sm transition-colors hover:bg-muted",
          open && "ring-2 ring-primary/30",
        )}
        aria-label={`Bench connection: ${statusLabel(controlLevel, loading)}`}
        title={statusLabel(controlLevel, loading)}
        onClick={() => setOpen((current) => !current)}
      >
        {loading || reloadPending ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <PlugZap
            className={cn(
              "h-4 w-4",
              controlLevel === "local" ? "text-emerald-600" : "text-amber-600",
            )}
          />
        )}
        <span
          className={cn(
            "absolute right-0.5 top-0.5 h-2 w-2 rounded-full border border-background",
            loading || reloadPending
              ? "bg-muted-foreground"
              : controlLevel === "local"
                ? "bg-emerald-500"
                : "bg-amber-500",
          )}
          aria-hidden
        />
      </button>

      {open ? (
        <FormAnchoredDetailCallout
          anchorSelector={anchorSelector}
          onClose={() => setOpen(false)}
          panelWidth={DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_WIDTH_PX}
          minContentHeight={DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_MIN_HEIGHT_PX}
          ariaLabel="Bench connection"
          measureKey={`${port}-${controlLevel}-${loading ? "loading" : "ready"}`}
        >
          <AnchoredDetailCalloutBody>
            <AnchoredDetailCalloutTitle>Bench connection</AnchoredDetailCalloutTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={tone}>{statusLabel(controlLevel, loading)}</Badge>
              {localBenchId ? (
                <span className="text-xs text-muted-foreground">
                  Bench <span className="font-medium text-foreground">{localBenchId}</span>
                </span>
              ) : null}
            </div>
            {controlMessage ? <p className="text-xs text-muted-foreground">{controlMessage}</p> : null}
            <AnchoredDetailCalloutFieldLabel label="Local port" htmlFor="bench-connection-port">
              <Input
                id="bench-connection-port"
                className="w-full"
                inputMode="numeric"
                value={draftPort}
                onChange={(event) => onDraftPortChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && portDirty) {
                    onApplyPort();
                  }
                }}
              />
            </AnchoredDetailCalloutFieldLabel>
          </AnchoredDetailCalloutBody>
          <AnchoredDetailCalloutFooter>
            <Button type="button" variant="secondary" size="sm" disabled={reloadPending} onClick={onReload}>
              {reloadPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Reload connection
            </Button>
            <Button type="button" size="sm" disabled={!portDirty || reloadPending} onClick={onApplyPort}>
              Apply port
            </Button>
          </AnchoredDetailCalloutFooter>
        </FormAnchoredDetailCallout>
      ) : null}
    </>
  );
}
